import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError, finalize, of, take } from 'rxjs';
import { BANNER_TYPE } from 'src/app/components/banner/banner.constants';
import { FileUploadResponse } from 'src/app/core/models/file-upload-results.model';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { environment } from 'src/app/environments/environment';
import {
  AddInterceptSummaryRequest,
  AddInterceptSummaryResponse,
  CancelInterceptSummaryRequest,
  CancelInterceptSummaryResponse,
  InterceptSummaryResponse,
} from '../../../models/position-operations/intercepts.model';
import { ErrorhandleService } from '../../error-handling/error-handle/errorhandle.service';

@Injectable({
  providedIn: 'root',
})
export class InterceptsService {
  private readonly _http = inject(HttpClient);
  private readonly _commonUtils = inject(CommonUtils);
  private readonly _errorHandler = inject(ErrorhandleService);

  readonly baseUrl = environment.apiUrl;

  /**
   * Get Intercepts
   * @return {*}  {Observable<InterceptSummaryResponse>}
   * @memberof InterceptsService
   */
  getIntercepts(): Observable<InterceptSummaryResponse> {
    this._commonUtils.updateLoadingModal(true);
    return this._http
      .get<InterceptSummaryResponse>(
        this.baseUrl.concat('/intercept/get-intercepts'),
      )
      .pipe(
        take(1),
        catchError((error) => {
          if (
            error.error.code === 400 &&
            error.error.message === 'No Data Available'
          ) {
            this._commonUtils.updateBannerType(
              BANNER_TYPE.INTERCEPT_DATA_UNAVAILABLE,
            );
            return of(null as unknown as InterceptSummaryResponse);
          } else {
            return this._errorHandler.handleError<InterceptSummaryResponse>(
              error,
              error.error.code,
              'Intercept Service: Add Local Intercept',
              BANNER_TYPE.TABLE_DATA_ERROR,
            );
          }
        }),
        finalize(() => {
          this._commonUtils.updateLoadingModal(false);
        }),
      );
  }

  addLocalIntercept(
    intercept: AddInterceptSummaryRequest,
  ): Observable<AddInterceptSummaryResponse> {
    this._commonUtils.updateLoadingModal(true);
    return this._http
      .post<AddInterceptSummaryResponse>(
        this.baseUrl.concat('/local-intercept/add'),
        intercept,
      )
      .pipe(
        take(1),
        catchError((error) =>
          this._errorHandler.handleError<AddInterceptSummaryResponse>(
            error,
            error.error.code,
            'Intercept Service: Add Local Intercept',
            BANNER_TYPE.API_400_ERROR,
          ),
        ),
        finalize(() => {
          this._commonUtils.updateLoadingModal(false);
        }),
      );
  }

  cancelLocalIntercept(
    intercept: CancelInterceptSummaryRequest,
  ): Observable<CancelInterceptSummaryResponse> {
    this._commonUtils.updateLoadingModal(true);
    return this._http
      .post<CancelInterceptSummaryResponse>(
        this.baseUrl.concat('/local-intercept/cancel'),
        intercept,
      )
      .pipe(
        take(1),
        catchError((error) =>
          this._errorHandler.handleError<CancelInterceptSummaryResponse>(
            error,
            error.error.code,
            'Intercept Service: Cancel Local Intercept',
            BANNER_TYPE.API_400_ERROR,
          ),
        ),
        finalize(() => {
          this._commonUtils.updateLoadingModal(false);
        }),
      );
  }

  uploadIntercept(
    trackingNumberList: string[],
  ): Observable<FileUploadResponse> {
    const body = {
      trackingNumber: trackingNumberList,
    };

    return this._http
      .post<FileUploadResponse>(
        this.baseUrl.concat('/local-intercept/multipleadd'),
        body,
      )
      .pipe(
        take(1),
        catchError((error) =>
          this._errorHandler.handleError<FileUploadResponse>(
            error,
            error.error.code,
            'Intercept Service: File upload Intercepts',
            BANNER_TYPE.API_400_ERROR,
          ),
        ),
      );
  }
}
